import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { CARDS, SC_COLORS } from "@/data/superCerveau";
import ScTempsForts from "./ScTempsForts";
import { Panel, Primary, Ghost, Chip, Field, XpPill, dateKey, parseKey, weekKey, weekDays, fmtDate, DAY_LETTERS, freshDay, dayXp, dayHasData, scaleOf, momentsOf, QuickAddRule, streakOf, STREAK_MILESTONES, COFFRE_EVERY } from "./scShared";

const STEPS = ["Les temps forts de la journée", "Ma jauge d'énergie", "Trouve ton super-pouvoir", "Trouve ton obstacle", "La montagne infranchissable", "Quelle astuce demain ?", "Mes XP du jour"];

const DayStrip = ({ selected, onSelect, days }) => {
  const today = dateKey();
  const wk = weekKey(parseKey(selected));
  return (
    <div className="flex gap-2 justify-center flex-wrap" data-testid="sc-day-strip">
      {weekDays(wk).map((k, i) => {
        const filled = dayHasData(days[k]);
        const future = k > today;
        return (
          <button key={k} disabled={future} onClick={() => onSelect(k)} data-testid={`sc-day-${k}`}
            className={`w-11 h-14 rounded-2xl border-2 flex flex-col items-center justify-center text-xs font-bold transition-all ${k === selected ? "bg-[var(--gold)] text-white border-[var(--gold)] scale-105" : future ? "opacity-30 border-[var(--border)]" : "bg-white border-[var(--border)] text-[var(--ink)] hover:border-[var(--gold)]"}`}>
            <span>{DAY_LETTERS[i]}</span>
            <span className="text-[10px] font-medium">{k.slice(8)}</span>
            {filled && <span className={`mt-0.5 w-1.5 h-1.5 rounded-full ${k === selected ? "bg-white" : "bg-[#7CC6A6]"}`} />}
          </button>
        );
      })}
    </div>
  );
};

const CardStep = ({ card, value, onPick, extra }) => {
  const c = SC_COLORS[card.color];
  return (
    <Panel img={card.img} title={`Carte « ${card.title} »`} sub={card.sub} color={card.color} testid={`sc-step-${card.id}`}>
      <p className="text-base italic font-medium mb-4 rounded-2xl px-4 py-3" style={{ background: c.bg, color: c.fg }}>🔎 « {card.question} »</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {extra.options.map((o) => <Chip key={o} color={card.color} active={value === o} onClick={() => onPick(value === o ? "" : o)} testid={`sc-opt-${card.id}-${o.replace(/\s+/g, "-").toLowerCase()}`}>{o}</Chip>)}
      </div>
      <Field value={extra.options.includes(value) ? "" : value} onChange={onPick} placeholder="Ou écris ta propre réponse…" testid={`sc-free-${card.id}`} />
      {extra.children}
    </Panel>
  );
};

export default function ScMission({ state, update }) {
  const [selected, setSelected] = useState(dateKey());
  const [step, setStep] = useState(0);
  const day = { ...freshDay(), ...(state.days[selected] || {}) };
  const setDay = (patch) => update((s) => ({ ...s, days: { ...s.days, [selected]: { ...freshDay(), ...(s.days[selected] || {}), ...patch } } }));
  const toggle = (field, id) => setDay({ [field]: day[field].includes(id) ? day[field].filter((x) => x !== id) : [...day[field], id] });
  const xp = dayXp(day, state);
  const ENERGY = scaleOf(state);
  const MOMENTS = momentsOf(state);
  const child = state.setup.childName || "toi";
  const next = () => { if (step < STEPS.length) { setStep(step + 1); window.scrollTo({ top: 0, behavior: "smooth" }); } };
  const prev = () => { if (step > 0) setStep(step - 1); };
  const finish = () => {
    setStep(STEPS.length); toast.success(`Mission du jour enregistrée : +${xp.enfant} XP pour ${child} !`);
    const st = streakOf({ ...state.days, [selected]: day });
    if (STREAK_MILESTONES.includes(st)) toast(`🔥 Série de ${st} missions du soir !`, { description: `Mini-badge « Série de ${st} missions » débloqué — bravo ${child} !`, duration: 8000 });
    if (st > 0 && st % COFFRE_EVERY === 0) toast(`🎁 Un coffre surprise t'attend !`, { description: `${st} soirs de série : ouvre-le dans « XP & récompenses ».`, duration: 8000 });
  };

  return (
    <div className="space-y-6" data-testid="sc-mission">
      <div className="text-center">
        <p className="text-sm text-[var(--ink-soft)] mb-3">Rituel du soir · 3 à 5 minutes · <strong className="text-[var(--ink)]">{fmtDate(selected)}</strong></p>
        <DayStrip selected={selected} onSelect={(k) => { setSelected(k); setStep(0); }} days={state.days} />
      </div>

      {step < STEPS.length && (
        <div className="flex items-center gap-2" data-testid="sc-progress">
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => setStep(i)} title={s} data-testid={`sc-progress-${i}`}
              className={`h-2.5 flex-1 rounded-full transition-all ${i <= step ? "bg-[var(--gold)]" : "bg-[var(--beige)]"}`} />
          ))}
        </div>
      )}

      {step === 0 && (
        <ScTempsForts day={day} setDay={setDay} moments={MOMENTS} scale={ENERGY} xpDevinette={state.xpDevinette ?? 1} setMoments={(m) => update((s) => ({ ...s, lists: { ...s.lists, moments: m } }))} childName={state.setup.childName} parentName={state.setup.parentName} />
      )}

      {step === 1 && (
        <Panel img="jauge-energie" title={`Étape 2 — « Ma jauge d'énergie »`} sub="Comment s'est passée ta journée ?" color="mint" testid="sc-step-energie">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {ENERGY.map((e) => (
              <button key={e.id} onClick={() => setDay({ energie: e.id })} data-testid={`sc-energie-${e.id}`}
                className={`rounded-3xl border-2 p-4 flex flex-col items-center gap-1 transition-all active:scale-95 ${day.energie === e.id ? "text-white scale-105 shadow-md" : "bg-white text-[var(--ink)] hover:scale-[1.02]"}`}
                style={{ borderColor: e.color, background: day.energie === e.id ? e.color : undefined }}>
                <span className="text-3xl">{e.emoji}</span><span className="text-sm font-bold">{e.label}</span>
              </button>
            ))}
          </div>
          <Field label="« Si ta journée était un jeu vidéo, tu serais arrivé à quel niveau ? »" value={day.niveau} onChange={(v) => setDay({ niveau: v })} placeholder="ex. niveau 7, le château de lave…" testid="sc-niveau" />
        </Panel>
      )}

      {step === 2 && <CardStep card={CARDS[0]} value={day.pouvoir} onPick={(v) => setDay({ pouvoir: v })} extra={{ options: state.lists.pouvoirs, children: <p className="mt-4 text-sm text-[var(--ink-soft)]">Trouver son super-pouvoir, c'est déjà <XpPill xp={1} /> à cocher à la dernière étape.</p> }} />}

      {step === 3 && <CardStep card={CARDS[1]} value={day.obstacle} onPick={(v) => setDay({ obstacle: v })} extra={{ options: state.lists.obstacles, children: <p className="mt-4 text-sm rounded-2xl bg-[var(--sand)] px-4 py-3 text-[var(--ink)]">L'objectif n'est pas de chercher un coupable. Le parent peut répondre : <em>« Ce n'est pas grave. On enquête. »</em></p> }} />}

      {step === 4 && (
        <CardStep card={CARDS[2]} value={day.boss} onPick={(v) => setDay({ boss: v })} extra={{ options: state.lists.boss, children: (
          <div className="mt-4 space-y-3">
            <Field label="« Est-ce qu'on sait pourquoi ton cerveau s'est bloqué ? »" value={day.bossCause} onChange={(v) => setDay({ bossCause: v })} placeholder="Si la réponse est non : mystère à résoudre cette semaine !" testid="sc-boss-cause" multiline />
            {!day.boss && <p className="text-sm text-[#2F7A55] font-medium">Pas de montagne infranchissable aujourd'hui ? Bravo, on passe à la suite !</p>}
          </div>
        ) }} />
      )}

      {step === 5 && <CardStep card={CARDS[3]} value={day.arme} onPick={(v) => setDay({ arme: v })} extra={{ options: state.lists.armes, children: <p className="mt-4 text-sm text-[var(--ink-soft)]">Parent + enfant choisissent ensemble. L'enfant peut proposer une idée. <strong>Une seule nouvelle arme à tester à la fois.</strong></p> }} />}

      {step === 6 && (
        <div className="grid md:grid-cols-2 gap-6">
          <Panel img="xp" title={`XP de ${child}`} sub="On ne gagne pas des points parce qu'on est « sage ». On gagne des XP parce qu'on utilise une stratégie." color="pouvoir" testid="sc-step-xp">
            <ul className="space-y-2">
              {state.xpEnfant.map((r) => (
                <li key={r.id}>
                  <button onClick={() => toggle("xpEnfant", r.id)} data-testid={`sc-xp-enfant-${r.id}`}
                    className={`w-full flex items-center justify-between gap-3 text-left text-sm px-4 py-2.5 rounded-2xl border-2 transition-all ${day.xpEnfant.includes(r.id) ? "bg-[#FFF3D6] border-[#F2B84B] text-[var(--ink)]" : "bg-white border-[var(--border)] text-[var(--ink)] hover:border-[#F2B84B]"}`}>
                    <span>{day.xpEnfant.includes(r.id) ? "✅" : "⬜"} {r.label}</span><XpPill xp={r.xp} />
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-3">
              <div className="text-xs font-semibold text-[var(--ink-soft)] mb-1.5">➕ Ajouter une option (gardée pour les prochaines fois)</div>
              <QuickAddRule color="pouvoir" placeholder="ex. J'ai préparé mon cartable seul." testid="sc-xp-enfant-quickadd" onAdd={(r) => { update((s) => ({ ...s, xpEnfant: [...s.xpEnfant, r] })); setDay({ xpEnfant: [...day.xpEnfant, r.id] }); toast.success("Option ajoutée et cochée pour aujourd'hui"); }} />
            </div>
            <div className="mt-4 text-right font-bold text-[#B8781A]" data-testid="sc-day-xp-enfant">Total du jour : +{xp.enfant} XP</div>
          </Panel>
          <Panel title={`XP de coéquipier — ${state.setup.parentName || "le parent"}`} sub="Le parent joue aussi. C'est essentiel." color="obstacle" testid="sc-step-xp-parent">
            <ul className="space-y-2">
              {state.xpParent.map((r) => (
                <li key={r.id}>
                  <button onClick={() => toggle("xpParent", r.id)} data-testid={`sc-xp-parent-${r.id}`}
                    className={`w-full flex items-center justify-between gap-3 text-left text-sm px-4 py-2.5 rounded-2xl border-2 transition-all ${day.xpParent.includes(r.id) ? "bg-[#E3F4F2] border-[#3FA7A3] text-[var(--ink)]" : "bg-white border-[var(--border)] text-[var(--ink)] hover:border-[#3FA7A3]"}`}>
                    <span>{day.xpParent.includes(r.id) ? "✅" : "⬜"} {r.label}</span><XpPill xp={r.xp} color="obstacle" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-3">
              <div className="text-xs font-semibold text-[var(--ink-soft)] mb-1.5">➕ Ajouter une option (gardée pour les prochaines fois)</div>
              <QuickAddRule color="obstacle" placeholder="ex. J'ai annoncé la fin de l'écran 10 min avant." testid="sc-xp-parent-quickadd" onAdd={(r) => { update((s) => ({ ...s, xpParent: [...s.xpParent, r] })); setDay({ xpParent: [...day.xpParent, r.id] }); toast.success("Option ajoutée et cochée pour aujourd'hui"); }} />
            </div>
            <div className="mt-4 text-right font-bold text-[#237572]" data-testid="sc-day-xp-parent">Total du jour : +{xp.parent} XP</div>
            <div className="mt-5 rounded-2xl bg-[var(--sand)] p-4">
              <div className="text-sm font-semibold text-[var(--ink)] mb-2">Défi Zéro rappel — rappels évités aujourd'hui</div>
              <div className="flex items-center gap-3">
                <button onClick={() => setDay({ rappelsEvites: Math.max(0, day.rappelsEvites - 1) })} className="w-9 h-9 rounded-full border border-[var(--gold)] text-[var(--gold)] font-bold" data-testid="sc-rappels-minus">−</button>
                <span className="text-2xl font-bold text-[var(--ink)] w-8 text-center" data-testid="sc-rappels-count">{day.rappelsEvites}</span>
                <button onClick={() => setDay({ rappelsEvites: day.rappelsEvites + 1 })} className="w-9 h-9 rounded-full bg-[var(--gold)] text-white font-bold" data-testid="sc-rappels-plus">+</button>
                <span className="text-xs text-[var(--ink-soft)]">1 stratégie utilisée seul = 1 rappel parental en moins.</span>
              </div>
            </div>
          </Panel>
        </div>
      )}

      {step === STEPS.length && (
        <Panel title="Mission du jour terminée !" color="mint" testid="sc-recap">
          {Object.keys(day.moments || {}).length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="sc-recap-moments">
              {MOMENTS.map((m) => { const d = (day.moments || {})[m.id]; if (!d || d.enfant === undefined) return null; const f = ENERGY.find((e) => e.id === (d.parent === d.enfant ? d.enfant : d.final)); return <span key={m.id} className="text-xs px-2.5 py-1.5 rounded-full bg-white border border-[var(--border)] text-[var(--ink)]">{m.emoji} {m.label.split(" (")[0]} {f ? f.emoji : "🤝"}</span>; })}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="rounded-2xl bg-[#FFF3D6] p-4"><div className="text-xs uppercase tracking-wider font-bold text-[#B8781A] mb-1">Énergie</div>{ENERGY.find((e) => e.id === day.energie)?.label || "—"} {day.niveau && <span className="text-[var(--ink-soft)]">· {day.niveau}</span>}</div>
            <div className="rounded-2xl bg-[#FFF3D6] p-4"><div className="text-xs uppercase tracking-wider font-bold text-[#B8781A] mb-1">Super-pouvoir</div>{day.pouvoir || "—"}</div>
            <div className="rounded-2xl bg-[#E3F4F2] p-4"><div className="text-xs uppercase tracking-wider font-bold text-[#237572] mb-1">Obstacle</div>{day.obstacle || "—"}</div>
            <div className="rounded-2xl bg-[#ECE6F7] p-4"><div className="text-xs uppercase tracking-wider font-bold text-[#5E4A9C] mb-1">Montagne infranchissable</div>{day.boss || "Aucune 🎉"}{day.bossCause && <div className="text-xs text-[var(--ink-soft)] mt-1">{day.bossCause}</div>}</div>
            <div className="rounded-2xl bg-[#FDE4DF] p-4 sm:col-span-2"><div className="text-xs uppercase tracking-wider font-bold text-[#B4483A] mb-1">Demain, on teste</div>{day.arme || "—"}</div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3 text-sm font-bold">
            <span className="px-4 py-2 rounded-full bg-[#F2B84B] text-white" data-testid="sc-recap-xp">⭐ {child} : +{xp.enfant} XP</span>
            <span className="px-4 py-2 rounded-full bg-[#3FA7A3] text-white">🤝 coéquipier : +{xp.parent} XP</span>
            {day.rappelsEvites > 0 && <span className="px-4 py-2 rounded-full bg-[#7CC6A6] text-white">🤫 {day.rappelsEvites} rappel(s) évité(s)</span>}
          </div>
          <div className="mt-6 flex gap-3 flex-wrap">
            <Ghost onClick={() => setStep(0)} data-testid="sc-recap-edit"><ChevronLeft size={16} /> Modifier</Ghost>
          </div>
        </Panel>
      )}

      {step < STEPS.length && (
        <div className="flex justify-between items-center gap-3 no-print">
          <Ghost onClick={prev} disabled={step === 0} className={step === 0 ? "invisible" : ""} data-testid="sc-prev"><ChevronLeft size={16} /> Précédent</Ghost>
          {step < STEPS.length - 1
            ? <Primary onClick={next} data-testid="sc-next">Suivant <ChevronRight size={16} /></Primary>
            : <Primary onClick={finish} data-testid="sc-finish"><Sparkles size={16} /> Valider la mission</Primary>}
        </div>
      )}
    </div>
  );
}
